import type { CSSProperties } from "react"
import { stickerColor } from "./stickerColors"
import styles from "@/styles/components/VoteToken.module.css"

/*
 * One vote, drawn as a sticker rather than a coloured dot — the blob shape
 * from `Base.jpg`, tinted by `--token-color`, matching the tray in
 * `Exercise.jpg`.
 *
 * `index` picks the colour, cycling `stickerColor`'s six hues — not tied to a
 * specific person or pain, just a position, so the same note or the same tray
 * slot always draws the same colour. `spent` swaps the colour for a neutral
 * one instead of hiding it behind opacity — the sticker empties out the way
 * the tray draws it, still legible as the same shape, just uncoloured.
 */

interface VoteTokenProps {
  index: number
  /** Already dragged onto a note — the tray slot it left behind, drawn as
   *  the same blob shape in a peeled, uncoloured finish
   *  rather than the coloured sticker, since there is no sticker sitting in
   *  the tray anymore. */
  spent?: boolean
  empty?: boolean
  /** The tray's tokens are the larger size; everywhere else — the mark a
   *  vote leaves on a note — is the small one. */
  size?: "sm" | "lg"
  /** Only set when the token stands for something specific — "Yours" on a
   *  voted note — rather than one of a row of otherwise-identical tokens. */
  label?: string
}

export default function VoteToken({ index, spent, empty, size = "sm", label }: VoteTokenProps) {
  const vars = {
    "--token-color": empty || spent ? "var(--paper-white)" : stickerColor(index),
  } as CSSProperties

  const className = [
    styles.token,
    size === "lg" ? styles.tokenLg : null,
    empty || spent ? styles.tokenEmpty : null,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <span
      className={className}
      style={vars}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  )
}
